import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Layers, Check, Sparkles, EyeOff, AlertTriangle } from "lucide-react";
import { ChipList } from "@/components/onboard/ChipList";

const seatBadge = (seat, t, on) => {
  if (!seat || typeof seat.minAvailable !== "number") return null;
  const a = seat.minAvailable;
  const tone = a <= 0 ? "red" : a <= 2 ? "amber" : "emerald";
  // On the selected (solid dark) chip use a solid white pill for contrast; otherwise a soft tint.
  const cls = on
    ? { red: "bg-white text-red-700 border-white", amber: "bg-white text-amber-700 border-white", emerald: "bg-white text-emerald-700 border-white" }[tone]
    : { red: "bg-red-100 text-red-700 border-red-300", amber: "bg-amber-100 text-amber-700 border-amber-300", emerald: "bg-emerald-100 text-emerald-700 border-emerald-300" }[tone];
  return (
    <span className={`ml-1 rounded-sm border px-1 py-0.5 text-[10px] font-bold ${cls}`} data-testid="lic-seat-badge">
      {a <= 0 ? t("onb.noSeatsBadge") : `${a} ${t("dash.seatFree")}`}
    </span>
  );
};

export const Step3Access = ({
  t, appOpts, sel, toggle, secGroups, m365Groups = [], dynamicGroups = [], distGroups, licenseGroups, licenses, teams,
  sharedMbx, toggleShared, setSharedAccess, mbxBadge, hiddenFromGal, setHiddenGal,
  seatByGroup = {}, noSeatGroups = [], groupName = (id) => id,
}) => (
  <div className="space-y-6">
    <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-zinc-600"><Layers size={15} /> {t("onboard.access")}</div>
    {appOpts.length > 0 && (
      <ChipList label={t("roles.applications")} options={appOpts} selected={sel.apps} onToggle={(id) => toggle("apps", id)} idKey="id" testid="chip-app" />
    )}
    <ChipList label={t("roles.securityGroups")} options={secGroups} selected={sel.groups} onToggle={(id) => toggle("groups", id)} idKey="id" testid="chip-group" />
    {m365Groups.length > 0 && (
      <ChipList label={t("roles.m365Groups")} options={m365Groups} selected={sel.groups} onToggle={(id) => toggle("groups", id)} idKey="id" testid="chip-m365" />
    )}
    <ChipList label={t("roles.distributionLists")} options={distGroups} selected={sel.dls} onToggle={(id) => toggle("dls", id)} idKey="id" testid="chip-dl" />
    {licenseGroups.length > 0 ? (
      <div>
        <ChipList label={t("roles.licenseGroups")} options={licenseGroups} selected={sel.groups} onToggle={(id) => toggle("groups", id)} idKey="id" testid="chip-licgroup"
          badge={(o, on) => seatBadge(seatByGroup[o.id], t, on)} />
        <p className="mt-1 text-[11px] text-zinc-400">{t("roles.licenseGroupHint")}</p>
        {noSeatGroups.length > 0 && (
          <div className="mt-2 flex items-start gap-2 border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-700" data-testid="onboard-noseat-warning">
            <AlertTriangle size={15} className="mt-0.5 shrink-0" />
            <div>
              <div className="font-bold">{t("onb.noSeatsWarnTitle")}</div>
              <div className="mt-0.5">{noSeatGroups.map(groupName).join(", ")}</div>
              <div className="mt-0.5 text-red-600">{t("onb.noSeatsWarnBody")}</div>
            </div>
          </div>
        )}
      </div>
    ) : (
      <ChipList label={t("roles.licensesDirect")} options={licenses} selected={sel.licenses} onToggle={(id) => toggle("licenses", id)} idKey="skuId" testid="chip-lic" />
    )}
    <ChipList label={t("roles.teams")} options={teams} selected={sel.teams} onToggle={(id) => toggle("teams", id)} idKey="id" testid="chip-team" />
    <div>
      <div className="mb-2 text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">{t("roles.sharedMailboxes")}</div>
      {sharedMbx.length === 0 ? <span className="text-sm text-zinc-400">{t("onb.noneAvailable")}</span> : (
        <div className="space-y-2">
          {sharedMbx.map((box) => {
            const cur = sel.shared.find((x) => x.id === box.id);
            return (
              <div key={box.id} className={`flex flex-wrap items-center gap-2 border px-3 py-2 ${cur ? "border-primary bg-primary/5" : "border-zinc-200"}`}>
                <button type="button" onClick={() => toggleShared(box)} data-testid={`chip-shared-${box.id}`}
                  className={`flex items-center gap-1.5 text-sm ${cur ? "font-semibold text-primary" : "text-zinc-700"}`}>
                  {cur && <Check size={14} />} {box.displayName}
                  <span className={`border px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${mbxBadge(box).cls}`} data-testid={`shared-type-${box.id}`}>{mbxBadge(box).label}</span>
                  <span className="font-mono text-xs text-zinc-400">{box.mail || box.userPrincipalName}</span>
                </button>
                {cur && (
                  <Select value={cur.access} onValueChange={(v) => setSharedAccess(box.id, v)}>
                    <SelectTrigger className="ml-auto h-8 w-48 rounded-sm border-zinc-300 text-xs" data-testid={`shared-access-${box.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fullAccess">{t("roles.fullAccess")}</SelectItem>
                      <SelectItem value="sendAs">{t("roles.sendAs")}</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
    {dynamicGroups.length > 0 && (
      <div data-testid="onboard-dynamic-preview">
        <div className="mb-1 flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.15em] text-violet-700"><Sparkles size={13} /> {t("access.autoTitle")}</div>
        <p className="mb-2 text-[11px] text-zinc-400">{t("access.autoHint")}</p>
        <div className="space-y-1.5">
          {dynamicGroups.map((g) => (
            <div key={g.id} className="flex items-start justify-between gap-3 border border-violet-200 bg-violet-50/60 px-3 py-2" data-testid={`onboard-dyn-${g.id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2 text-sm font-medium text-zinc-800">{g.displayName}
                  <span className="border border-violet-300 bg-violet-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-violet-700">{t("access.autoBadge")}</span></div>
                {(g.membershipRule || g.recipientFilter) && <div className="mt-0.5 break-all font-mono text-[10px] text-zinc-500">{g.membershipRule || g.recipientFilter}</div>}
              </div>
            </div>
          ))}
        </div>
      </div>
    )}
    <div className="flex items-center justify-between border border-zinc-200 px-3 py-2.5" data-testid="onboard-hidegal-row">
      <div className="flex items-center gap-2 pr-3">
        <EyeOff size={15} className="text-zinc-500" />
        <div className="text-sm font-medium text-zinc-800">{t("onb.hideGal")}</div>
      </div>
      <Switch checked={!!hiddenFromGal} onCheckedChange={setHiddenGal} data-testid="onboard-hidegal" />
    </div>
  </div>
);
